import { useEffect, useState } from "react";
import { Fingerprint, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { registerPasskey, passkeySupported, signInWithPasskey, hasPasskeyDevice } from "@/lib/passkey";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Register / list / remove device passkeys (Face ID, fingerprint, Windows Hello). staffId binds a passkey to a team member.
export const PasskeyManager = ({ accent = "bg-[#1E3A8A] hover:bg-[#152a63]", iconColor = "text-emerald-600", staffId, staffName }) => {
  const [list, setList] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/auth/passkey").then((r) => setList(staffId ? r.data.filter((p) => p.staff_id === staffId) : r.data)).catch(() => {});
  useEffect(() => { load(); }, [staffId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setBusy(true);
    try { await registerPasskey(label, staffId); toast.success(`Passkey added${staffName ? ` for ${staffName}` : ""} — one-touch sign-in is ready`); setLabel(""); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/auth/passkey/${id}`); toast.success("Passkey removed"); setConfirm(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-3" data-testid="passkey-manager">
      <p className="font-extrabold text-slate-900 flex items-center gap-2"><Fingerprint className={`w-5 h-5 ${iconColor}`} /> Register Device Biometrics / Passkey</p>
      <p className="text-xs text-slate-500">{staffName ? `Do this on ${staffName}'s own device. Their passkey signs them in directly as ${staffName}; their 4-digit PIN still works as a fallback.` : "Bind this device to the profile you're signed in as, so it signs you straight in with Face ID, fingerprint or your device screen lock. Your PIN and password still work as a fallback."} Passkeys work only on the web address where they were added.</p>
      {!passkeySupported() ? (
        <p data-testid="passkey-unsupported" className="text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg p-2.5">This browser doesn't support passkeys. Try Safari, Chrome or Edge on a device with a screen lock.</p>
      ) : (
        <>
          <Input data-testid="passkey-label" placeholder="Device name (optional, e.g. Shop iPhone)" value={label} onChange={(e) => setLabel(e.target.value)} className="h-11" />
          <Button data-testid="passkey-register-btn" disabled={busy} onClick={add} className={`w-full h-11 ${accent} text-white font-bold gap-2`}><Fingerprint className="w-4 h-4" /> {busy ? "Waiting for your device…" : staffName ? `Register this device for ${staffName}` : "Register this device"}</Button>
        </>
      )}
      <div className="space-y-2">
        {list.map((p) => (
          <div key={p.id} data-testid={`passkey-row-${p.id}`} className="flex items-center gap-2 p-2.5 rounded-xl border border-slate-200">
            <Fingerprint className="w-4 h-4 text-slate-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-slate-900 truncate" data-no-translate>{p.label}</p>
              <p data-testid={`passkey-who-${p.id}`} className="text-[11px] font-semibold text-blue-700 truncate" data-no-translate>{p.staff_name ? `Signs in as ${p.staff_name}` : "Whole store · choose profile + PIN"}</p>
              <p className="text-[11px] text-slate-500 truncate">{p.rp_id}{p.rp_id === window.location.hostname ? "" : " · other web address"}{p.last_used_at ? ` · last used ${new Date(p.last_used_at).toLocaleDateString("en-GB")}` : ""}</p>
            </div>
            {confirm === p.id ? (
              <div className="flex gap-1.5 shrink-0">
                <Button data-testid={`passkey-remove-confirm-${p.id}`} onClick={() => remove(p.id)} className="h-8 px-2.5 text-xs bg-red-600 hover:bg-red-700 text-white">Remove</Button>
                <Button data-testid={`passkey-remove-cancel-${p.id}`} variant="outline" onClick={() => setConfirm(null)} className="h-8 px-2.5 text-xs">Cancel</Button>
              </div>
            ) : (
              <button data-testid={`passkey-remove-${p.id}`} onClick={() => setConfirm(p.id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 shrink-0"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        ))}
        {!list.length && <p data-testid="passkey-empty" className="text-xs text-slate-400">No passkeys registered yet.</p>}
      </div>
    </div>
  );
};

// auto: on a device that has used a passkey before, open the biometric prompt as soon as the page loads (once per visit).
let autoFired = false;
export const PasskeySignInButton = ({ onDone, onBlocked, className = "", testid = "passkey-signin-btn", auto = false, app = "admin" }) => {
  const [busy, setBusy] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const supported = passkeySupported();
  const go = async (silent = false) => {
    setBusy(true); setCancelled(false);
    try { await onDone(await signInWithPasskey(app)); }
    catch (e) {
      if (onBlocked && e.message?.startsWith("Your store has been deleted")) onBlocked();
      else if (e.cancelled || silent) setCancelled(true);
      else toast.error(e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => {
    if (auto && supported && hasPasskeyDevice() && !autoFired) { autoFired = true; go(true); }
  }, [auto, supported]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!supported) return null;
  return (
    <div className="space-y-1">
      <Button data-testid={testid} type="button" variant="outline" disabled={busy} onClick={() => go()} className={`w-full h-12 font-bold gap-2 ${className}`}>
        <Fingerprint className="w-5 h-5" /> {busy ? "Waiting for your device…" : "Sign in with Face ID / Fingerprint"}
      </Button>
      {cancelled && <p data-testid={`${testid}-fallback`} className="text-[11px] text-center text-slate-500">Face ID / fingerprint didn't complete. Tap above to try again, or use your password.</p>}
    </div>
  );
};
